import {
  type FC,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useTranslation } from "react-i18next";
import {
  isTimeValid,
  secondsToTimeString,
  timeStringToSeconds,
} from "./timeUtils";

interface TimeInputProps {
  /** Значение в секундах. */
  value: number;
  onChange: (seconds: number) => void;
  label?: string;
  videoDuration?: number;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  required?: boolean;
  disabled?: boolean;
}

// Порт референсного VideoForm/Timecodes/TimeInput.js. Отличие: без
// react-input-mask (несовместим с React 19) — та же семантика (MM:SS /
// HH:MM:SS при видео ≥ часа, стрелки ±1 сек, clamp 0..duration, коррекция
// формата на blur) на обычном TextField с фильтрацией ввода.
export const TimeInput: FC<TimeInputProps> = ({
  value,
  onChange,
  label,
  videoDuration = 0,
  error,
  helperText,
  fullWidth = true,
  required = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [displayValue, setDisplayValue] = useState("");

  const useHours = videoDuration >= 3600;
  // Плейсхолдер с подсказкой о максимуме: «ММ:СС (макс. 12:34)»
  // (референсный createPlaceholder, маска — через переводы).
  const timeMask = useHours
    ? t("videos.timecodes.timeMaskHours")
    : t("videos.timecodes.timeMask");
  const placeholder =
    videoDuration > 0
      ? t("videos.timecodes.timePlaceholderMax", {
          mask: timeMask,
          max: secondsToTimeString(videoDuration, useHours),
        })
      : timeMask;
  const maxLength = useHours ? 8 : 5;

  // Синхронизация с внешним value (в секундах): пока набранный текст
  // парсится в то же значение — не трогаем его, иначе реформат на каждом
  // нажатии ломает ввод («00:1» → эффект переписывает в «00:01», следующая
  // цифра встаёт не туда). Реформат нужен только на внешние изменения
  // (стрелки ±1 сек, инициализация формы).
  useEffect(() => {
    setDisplayValue((prev) => {
      if (prev && timeStringToSeconds(prev, useHours) === value) return prev;
      if (!prev && value === 0) return prev;
      return value > 0 ? secondsToTimeString(value, useHours) : "";
    });
  }, [value, useHours]);

  const handleTimeChange = useCallback(
    (delta: number) => {
      const newValue = Math.max(
        0,
        Math.min((value || 0) + delta, videoDuration || Infinity)
      );
      onChange(newValue);
    },
    [value, onChange, videoDuration]
  );

  const handleInputChange = (inputValue: string) => {
    // Разрешаем только цифры и двоеточия — аналог маски 99:99(:99)
    const cleaned = inputValue.replace(/[^\d:]/g, "").slice(0, maxLength);
    setDisplayValue(cleaned);
    onChange(timeStringToSeconds(cleaned, useHours));
  };

  const handleBlur = () => {
    // Корректируем формат при потере фокуса (референсный handleBlur)
    if (displayValue && displayValue.replace(/[_:]/g, "").trim() !== "") {
      const seconds = timeStringToSeconds(displayValue, useHours);
      setDisplayValue(secondsToTimeString(seconds, useHours));
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "ArrowUp") {
      event.preventDefault();
      handleTimeChange(1);
    } else if (event.key === "ArrowDown") {
      event.preventDefault();
      handleTimeChange(-1);
    }
  };

  const isIncrementDisabled =
    disabled || (videoDuration > 0 && (value || 0) >= videoDuration);
  const isDecrementDisabled = disabled || (value || 0) <= 0;

  const isValid = isTimeValid(displayValue, videoDuration, useHours);
  const hasError = Boolean(error) || !isValid;
  const errorMessage = !isValid
    ? t("videos.timecodes.timeExceedsVideo", {
        maxTime: secondsToTimeString(videoDuration, useHours),
      })
    : helperText;

  return (
    <TextField
      value={displayValue}
      onChange={(event) => handleInputChange(event.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      label={label}
      fullWidth={fullWidth}
      variant="outlined"
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      error={hasError}
      helperText={errorMessage}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <IconButton
                size="small"
                onClick={() => handleTimeChange(1)}
                disabled={isIncrementDisabled}
                aria-label={t("videos.timecodes.increaseSecondAria")}
                sx={{ p: "2px", "& .MuiSvgIcon-root": { fontSize: 16 } }}
              >
                <KeyboardArrowUpIcon />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleTimeChange(-1)}
                disabled={isDecrementDisabled}
                aria-label={t("videos.timecodes.decreaseSecondAria")}
                sx={{ p: "2px", "& .MuiSvgIcon-root": { fontSize: 16 } }}
              >
                <KeyboardArrowDownIcon />
              </IconButton>
            </Box>
          </InputAdornment>
        ),
      }}
    />
  );
};
